import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Users, 
  Target, 
  Lightbulb, 
  Award,
  CheckCircle
} from 'lucide-react';

const ScrollSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
};

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Strategy First',
      description: 'Every project begins with deep research and clear objectives. We don\'t create for the sake of creating.',
    },
    {
      icon: Lightbulb,
      title: 'Creative Excellence',
      description: 'We push creative boundaries while staying true to your brand. Quality is never compromised.',
    },
    {
      icon: Users,
      title: 'Partnership Mindset',
      description: 'We work alongside you, not just for you. Your success is our success.',
    },
    {
      icon: Award,
      title: 'Results Obsessed',
      description: 'Everything we do is measured. We optimize relentlessly for outcomes that matter.',
    },
  ];

  const timeline = [
    { year: '2008', title: 'Founded', description: 'Started as a video production house in Pune' },
    { year: '2012', title: 'Digital Expansion', description: 'Added digital marketing services to our portfolio' },
    { year: '2016', title: 'Education Focus', description: 'Became the go-to agency for educational institutions' },
    { year: '2020', title: 'Tech Integration', description: 'Launched web development and tech solutions' },
    { year: '2024', title: 'Growth Partner', description: 'Evolved into full-service digital growth agency' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">About Us</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              We Help Brands Build{' '}
              <span className="text-gradient">Digital Momentum</span>
            </h1>
            <p className="text-fp-textMuted text-lg leading-relaxed">
              Since 2008, Focus Pixel has been at the intersection of creativity and strategy, 
              helping ambitious brands transform their digital presence into a powerful growth engine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollSection>
              <div className="gradient-border p-1">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Focus Pixel Office"
                  className="rounded-xl w-full"
                />
              </div>
            </ScrollSection>

            <ScrollSection delay={0.2}>
              <div>
                <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Story</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  From Video Production to Digital Growth Partner
                </h2>
                <p className="text-fp-textMuted leading-relaxed mb-6">
                  What started as a passion for storytelling through video has evolved into a 
                  comprehensive digital agency. Founded by Mayur Dhanwani in Pune, Focus Pixel 
                  began with a simple belief: great content can transform businesses.
                </p>
                <p className="text-fp-textMuted leading-relaxed mb-6">
                  Over 15 years, we've expanded our capabilities to meet the evolving needs of 
                  our clients. Today, we're a full-service digital agency that combines creative 
                  excellence with data-driven strategy.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-3xl font-bold text-white">15+</p>
                    <p className="text-fp-textMuted text-sm">Years in Business</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-white">200+</p>
                    <p className="text-fp-textMuted text-sm">Clients Served</p>
                  </div>
                </div>
              </div>
            </ScrollSection>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-fp-surface/30">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Values</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                What Drives Us
              </h2>
              <p className="text-fp-textMuted text-lg">
                The principles that guide every decision and deliverable.
              </p>
            </div>
          </ScrollSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <ScrollSection key={value.title} delay={index * 0.1}>
                <div className="glass-card p-8 rounded-2xl">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                  <p className="text-fp-textMuted leading-relaxed">{value.description}</p>
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Journey</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Building Over Time
              </h2>
            </div>
          </ScrollSection>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/50 to-secondary/50" />

            {timeline.map((item, index) => (
              <ScrollSection key={item.year} delay={index * 0.1}>
                <div className={`relative flex items-center gap-8 mb-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}>
                  {/* Timeline Dot */}
                  <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-primary rounded-full -translate-x-1/2 z-10" />

                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:pl-12'}`}>
                    <div className="glass-card p-6 rounded-xl inline-block">
                      <span className="text-primary font-bold text-lg">{item.year}</span>
                      <h3 className="text-white font-semibold text-xl mt-2 mb-2">{item.title}</h3>
                      <p className="text-fp-textMuted">{item.description}</p>
                    </div>
                  </div>

                  <div className="hidden md:block flex-1" />
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-fp-surface/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollSection>
              <div>
                <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Leadership</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Meet Our Founder
                </h2>
                <p className="text-fp-textMuted leading-relaxed mb-6">
                  Mayur Dhanwani founded Focus Pixel with a vision to bridge the gap between 
                  creative storytelling and business results. With over 15 years of experience 
                  in video production and digital marketing, he leads a team of passionate 
                  creatives and strategists.
                </p>
                <ul className="space-y-3">
                  {[
                    'Expert in video production & cinematography',
                    'Specialized in educational sector marketing',
                    'Proven track record in brand transformation',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-fp-textMuted">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollSection>

            <ScrollSection delay={0.2}>
              <div className="gradient-border p-1">
                <img
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=80"
                  alt="Mayur Dhanwani"
                  className="rounded-xl w-full"
                />
              </div>
            </ScrollSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <ScrollSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Partner With Us?
            </h2>
            <p className="text-fp-textMuted text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help transform your digital presence 
              and drive meaningful growth for your brand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-call"
                className="btn-primary flex items-center justify-center gap-2 text-base"
                data-testid="about-cta-primary"
              >
                Book a Strategy Call
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/portfolio"
                className="btn-secondary flex items-center justify-center gap-2 text-base"
                data-testid="about-cta-secondary"
              >
                View Our Work
              </Link>
            </div>
          </ScrollSection>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
